import mixpanelPlugin from "@analytics/mixpanel";
// eslint-disable-next-line no-restricted-imports
import Analytics from "analytics";
import { isEqual } from "lodash-es";
// eslint-disable-next-line no-restricted-imports
import {
    useTracking as _useTracking,
    type Options,
    track,
    type Track,
    type TrackingHook,
    type TrackingProp,
} from "react-tracking";

import { BTAnalyticsEvent, type IBTTrackingData } from "@btnet/core-components/analytics";

import { type UserInfo } from "helpers/AppProvider.types";

import { BTLocalStorage, BTSessionStorage } from "types/btStorage";
import { type EntityEventType, ImpersonationTypes } from "types/enum";

import {
    type AnalyticsBuilderTraits,
    type AnalyticsTraits,
} from "utilities/analytics/analytics.api.types";
import { getAnalyticsConfig } from "utilities/analytics/analyticsConfig";
import { translateRouteParams } from "utilities/analytics/analyticsUtils";
import { qualtricsPlugin } from "utilities/analytics/customPlugins/qualtricsPlugin";
import { sawmillPlugin } from "utilities/analytics/customPlugins/sawmillPlugin";
import { getMixpanelOverrides } from "utilities/analytics/mixpanelOverrides";
import { getCurrentPortalType, PortalType } from "utilities/portal/portal";
import { routes } from "utilities/routes";

export const allowedAnalyticsEvents = Object.keys(BTAnalyticsEvent);
export type AnalyticsEvent = keyof typeof BTAnalyticsEvent;

export interface ITrackingData extends Omit<Partial<IBTTrackingData>, "event"> {
    jobId: number | undefined;
    builderId: number | undefined;
    page: string | undefined;
    modal: string | undefined;
    panel: string | undefined;
    tab: string | undefined;
    entityType: EntityEventType | undefined;
    component: string | undefined;
    element: string | undefined;
    uniqueId: string | undefined;
    event: AnalyticsEvent | undefined;
    value: string | number | undefined;
    extraInfo: any | undefined;
    // On SPA, this can be captured by just the page view events, but until then, we'll manually
    // track the route that the page is on to account for the use of memory routers.
    route: string | undefined;
    routeParams: any | undefined;
    multitab: boolean | undefined;
    userType: number | undefined;
    impersonation: number | undefined;
    /**
     * DO NOT SET THIS VALUE. THIS SHOULD ONLY BE USED BY AppProvider.
     */
    pageView: boolean | undefined;
}

export interface ITrackingProp {
    tracking?: TrackingProp<ITrackingData>;
}

function getAnalytics() {
    const plugins = getPlugins();

    return Analytics({
        app: "Web App",
        debug: import.meta.env.MODE === "development",
        plugins,
    });
}

function getPlugins() {
    const plugins: Record<string, unknown>[] | undefined = [];
    const disableAnalytics = BTLocalStorage.get("bt-boolean-disableAllAnalytics");

    if (disableAnalytics) {
        return plugins;
    }

    const { mixpanel, sawmill } = getAnalyticsConfig();

    // Mixpanel
    if (mixpanel.enabled) {
        const plugin = mixpanelPlugin({
            token: mixpanel.projectToken,
            pageEvent: "PageView",
            options: mixpanel.apiHost ? { api_host: mixpanel.apiHost } : null,
        });
        plugins.push({
            ...plugin,
            ...getMixpanelOverrides(plugin),
        });
    }

    // Qualtrics. Registered unconditionally because this client is built once and cached
    // forever, while `window.QSI` is set by a script injected after the page loads — gating
    // registration on it would lose the race and drop Qualtrics for the whole session. The
    // plugin no-ops per event until QSI is ready.
    plugins.push({
        ...qualtricsPlugin({}),
    });

    // Sawmill
    if (sawmill.enabled) {
        plugins.push(
            sawmillPlugin({
                tenantId: sawmill.tenantId,
                ingestionEndpoint: sawmill.ingestionEndpoint,
                batchThreshold: sawmill.batchThreshold,
                maxCachedEvents: sawmill.maxCachedEvents,
                maxPayloadSizeBytes: sawmill.maxPayloadSizeBytes,
            }),
        );
    }

    return plugins;
}

/**
 * The analytics client, built on first use rather than at module load.
 *
 * Its config comes from the init endpoint (ADO 282593), which resolves after this module is
 * imported — ~240 files import it, so building eagerly would read the config before it lands.
 * Every entry point below goes through here, and all of them run in response to a user action or
 * a mounted `AppProvider`, by which point the payload has resolved.
 */
let analyticsClient: ReturnType<typeof getAnalytics> | undefined;

function getAnalyticsClient() {
    analyticsClient ??= getAnalytics();
    return analyticsClient;
}

export function identify(globalUserId: number, traits: AnalyticsTraits) {
    void getAnalyticsClient().identify(globalUserId.toString(), traits);
}

export function resetAnonymousUserIdentity() {
    const analytics = getAnalyticsClient();
    const userId = analytics.user("userId");
    if (userId !== null && userId === 0) {
        void analytics.reset();
    }
}

export function shouldIdentifyUser(globalUserId: number | null): boolean {
    const userId = getAnalyticsClient().user("userId");
    if (userId == null) {
        return true;
    }
    return globalUserId !== null && globalUserId !== userId;
}

export { track };
export type { Track };
export const useTracking = (
    trackingData?: Partial<ITrackingData>,
    options?: Partial<Options<ITrackingData>>,
): TrackingHook<ITrackingData> => _useTracking(trackingData, options);

export function dispatchTracking(data: Partial<ITrackingData>) {
    const { event, routeParams, ...trackingData } = data;

    const analytics = getAnalyticsClient();
    const traits = analytics?.getState?.().user?.traits;
    const builderTraits: AnalyticsBuilderTraits = traits?.builderTraits;
    const userTraits: UserInfo = traits?.userTraits;

    const sessionStorageJobPickerState = BTSessionStorage.get("bt-object-dangerousJobPickerState");
    const localStorageJobPickerState = BTLocalStorage.get("bt-object-dangerousJobPickerState");

    const ssSelectedJobIds = sessionStorageJobPickerState?.selectedJobIds;
    const lsSelectedJobIds = localStorageJobPickerState?.selectedJobIds;

    let multitab = false;
    if (ssSelectedJobIds && lsSelectedJobIds && !isEqual(ssSelectedJobIds, lsSelectedJobIds)) {
        multitab = true;
    }

    const roleInfo = userTraits?.roleInfo;

    let impersonationType = ImpersonationTypes.None;
    if (userTraits?.isImpersonatingBuilder) {
        impersonationType = ImpersonationTypes.BTAdmin;
    } else if (userTraits?.isBuilderImpersonatingOwner) {
        impersonationType = ImpersonationTypes.BuilderImpersonatingOwner;
    }

    const payload = {
        // Flatten any route params, typically includes id and jobId
        ...translateRouteParams(routeParams),
        // Populate builderId in case it wasn't provided in the supplied event data.
        builderId: builderTraits?.builderId,
        // And spread the original tracking data. This may override properties from routeParams
        // and the builderId, but we'll assume the trackingData is the most correct if defined
        multitab: multitab,
        portalType: PortalType[getCurrentPortalType()],
        impersonationType: ImpersonationTypes[impersonationType],
        // Spread into a plain object: this payload is persisted to localStorage by the Sawmill
        // plugin, and BTStorage rejects any value whose constructor isn't Object. Falling back
        // to the original value rather than null keeps the serialized payload byte-identical -
        // an absent roleInfo must stay absent, not become an explicit null.
        roleInfo: roleInfo ? { ...roleInfo } : roleInfo,
        ...trackingData,
    };

    if (event === "PageView") {
        void analytics.page(payload as Partial<ITrackingData>);
    } else {
        void analytics.track((event ?? "Undefined") as string, payload);
    }
}

export function processTracking(data: Partial<ITrackingData>): Partial<ITrackingData> | null {
    return data.pageView ? { event: "PageView" } : null;
}

/**
 * Routes whose page components manage their own PageView event.
 * AppProvider skips its global PageView when the current pathname matches.
 */
export function hasOwnPageViewTracking(pathname: string): boolean {
    const ownPageViewRoutes = [
        routes.budget.getListLink(),
        routes.jobCostingBudget.getLink(),
        routes.onlinePayments.getPaymentsOverviewLink(),
    ];
    return ownPageViewRoutes.some((route) => pathname.endsWith(route));
}
